"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchConfirmationDialog } from "@/fetch-ui/FetchConfirmationDialog";
import { FetchInput } from "@/fetch-ui/FetchInput";
import FetchModal, { modalStack } from "@/fetch-ui/FetchModal";
import { normalizeUserSearch } from "@/features/admin-dash/_logic/admin-users-search";
import { useDebounce } from "@/hooks/useDebounce";
import { useUserStore } from "@/lib/stores/userStore";
import { IMPERSONATE_SEARCH_DEBOUNCE_MS } from "../../_logic/constants";
import type { ImpersonatableUser } from "../../_logic/db";
import { Search, User } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImpersonateUserRow } from "./ImpersonateUserRow";
import { ImpersonateUserRowSkeleton } from "./ImpersonateUserRowSkeleton";

export const ImpersonateUserModal = ({
  searchUsers,
  impersonateUser,
}: {
  searchUsers: (search: string) => Promise<ImpersonatableUser[]>;
  impersonateUser: (email: string, redirectTo?: string) => Promise<string>;
}) => {
  // Hooks
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const shouldHideForAvaInbox = pathname?.startsWith("/ava/inbox");

  // State
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [users, setUsers] = useState<ImpersonatableUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<ImpersonatableUser | null>(null);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Derived values
  // Normalized the way the server reads it, so a keystroke that cannot change the results (a
  // trailing space, a one-character term) does not fire another search.
  const searchTerm = normalizeUserSearch(useDebounce(searchQuery, IMPERSONATE_SEARCH_DEBOUNCE_MS));

  // Effects
  // Search runs on the server: the user table is too large to ship to the browser and filter here.
  useEffect(() => {
    if (!isOpen) return;

    // Drops a response that lands after a newer search started, so results never go back in time.
    let isStale = false;
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const fetchedUsers = await searchUsers(searchTerm);
        if (!isStale) setUsers(fetchedUsers);
      } catch (error) {
        console.error("Error searching users:", { search: searchTerm }, error);
        if (!isStale) setUsers([]);
      } finally {
        if (!isStale) setIsLoading(false);
      }
    };

    fetchUsers();
    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm]);

  // Handlers
  const handleCancelImpersonate = useCallback(() => {
    setShowConfirmation(false);
    setSelectedUser(null);
  }, []);

  // FetchConfirmationDialog (Headless UI) never joins FetchModal's shared modalStack on its own, so
  // without this, a single Escape would close both this confirmation AND the search modal beneath
  // it — see DiscardDraftConfirmDialog for the same pattern.
  const cancelRef = useRef(handleCancelImpersonate);
  cancelRef.current = handleCancelImpersonate;
  useEffect(() => {
    if (!showConfirmation) return;
    const sentinel = (): void => cancelRef.current();
    modalStack.push(sentinel);
    return () => modalStack.remove(sentinel);
  }, [showConfirmation]);

  const handleCloseModal = () => {
    setIsOpen(false);
    setSearchQuery("");
    triggerRef.current?.focus();
  };

  const handleImpersonateClick = useCallback(
    (user: ImpersonatableUser) => {
      if (isImpersonating) return;
      setSelectedUser(user);
      setShowConfirmation(true);
    },
    [isImpersonating]
  );

  const handleConfirmImpersonate = async () => {
    if (!selectedUser) return;

    setIsImpersonating(true);
    try {
      const searchParamsString = searchParams.toString();
      const redirectTo = searchParamsString ? `${pathname}?${searchParamsString}` : pathname;

      // Client side actions
      useUserStore.getState().clearUser();
      posthog?.reset();

      // Server side action
      const callbackUrl = await impersonateUser(selectedUser.email, redirectTo);

      // Client side navigation
      window.location.href = callbackUrl;
    } catch (error) {
      console.error("Error impersonating user:", error);
      // sonner, not useToast/showToast: this component mounts from the root layout, which has no
      // FetchToastProvider (that's only mounted per-route) — useToast() would throw here.
      toast.error("Failed to impersonate user");
      setIsImpersonating(false);
    }
  };

  // Helpers
  const getUserDisplayName = (user: ImpersonatableUser | null): string => {
    if (!user) return "";
    if (user.full_name) return user.full_name;
    const nameParts = [user.first_name, user.last_name].filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(" ");
    return user.email;
  };

  if (shouldHideForAvaInbox) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-24 right-4 z-50 rounded-full shadow-lg">
        <FetchButton
          onClick={() => {
            triggerRef.current = document.activeElement as HTMLElement;
            setIsOpen(true);
          }}
          variant="secondary"
          iconOnly
          leftIcon={<User className="w-5 h-5" />}
          tooltip="Impersonate user"
          className="!rounded-full"
        />
      </div>

      <FetchModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        title="Impersonate user"
        subtitle="Search for a user to impersonate their account"
        size="auto"
        className="w-full max-w-md"
        // FetchModal's content wrapper is overflow-hidden with zero built-in padding, so a
        // focused FetchInput's ring/glow (which renders outside its own border box) gets clipped
        // flush on every edge. Padding + matching negative margin buys a buffer inside the clip
        // region without shifting the content's on-screen position.
        contentClassName="p-1.5 -m-1.5"
      >
        <div className="space-y-4 min-w-0">
          <FetchInput
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
            autoFocus
          />

          <div className="h-[400px] overflow-y-auto space-y-2" aria-busy={isLoading}>
            {isLoading ? (
              <ImpersonateUserRowSkeleton />
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm font-medium">No users found</p>
              </div>
            ) : (
              users.map((user) => (
                <ImpersonateUserRow key={user.user_id} user={user} handleImpersonate={handleImpersonateClick} />
              ))
            )}
          </div>
        </div>
      </FetchModal>

      <FetchConfirmationDialog
        isOpen={showConfirmation}
        onClose={handleCancelImpersonate}
        onConfirm={handleConfirmImpersonate}
        title="Confirm impersonation?"
        message={`You will be logged out of your current account to impersonate ${getUserDisplayName(selectedUser)}.`}
        type="info"
        isLoading={isImpersonating}
        // FetchModal (still open behind this) defaults to zIndex 99999/100000; without this override
        // the confirmation would paint behind it at FetchConfirmationDialog's own z-50 default.
        zIndexClass="z-[100001]"
      />
    </>
  );
};
